import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import {
  checkNonMoneyDataForTable,
  trimObject,
  dateTimeFormatter,
} from "utils/utils";
import { queryMerchant } from "./model";
import { isAllInputValid } from "utils/formValidation";
import s from "./OnlineMerchants.module.scss";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import ColumnFilter from "components/ColumnFilter/ColumnFilter";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import moment from "moment";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";

import merchantManagement from "routes/merchantManagement";
import transactions from "routes/transactions";
import tools from "routes/tools";

import AgentStatusOptions from "utils/AgentStatusOptions/AgentStatusOptions";

const PAGE_LOCALE_KEY = "page.merchants.merchantList.";

const INPUT_FIELD_INIT_STATE = {
  merchantId: "",
  status: "",
  businessRegistration: "",
  email: "",
  isoAgentId: "",
  name: "",
  phone: "",
  terminalSerialNumber: "",
};

const INPUT_FIELD_ID_OVERRIDE_STATE = {
  status: "",
  businessRegistration: "",
  email: "",
  isoAgentId: "",
  name: "",
  phone: "",
  terminalSerialNumber: "",
};

const ERROR_INIT_STATE = {
  merchantId: false,
  status: false,
  businessRegistration: false,
  email: false,
  isoAgentId: false,
  name: false,
  phone: false,
  terminalSerialNumber: false,
};

const OnlineMerchants = ({ redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "OnlineMerchantListGrid";

  const [searching, setSearching] = useState(false);

  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState(
    INPUT_FIELD_INIT_STATE,
  );

  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);
  const [isIdInput, setIsIdInput] = useState(false);

  const [columnDisplayDialog, setColumnDisplayDialog] = useState({
    open: false,
    position: 0,
  });
  const [columnsArray, setColumnsArray] = useState([]);

  const [queryMerchantLoading, setQueryMerchantLoading] = useState(true);
  useEffect(() => {
    if (searchFieldInput.merchantId !== "" && !isIdInput) {
      setIsIdInput(true);
      setSearchFieldInput({
        ...searchFieldInput,
        ...INPUT_FIELD_ID_OVERRIDE_STATE,
      });
    } else if (searchFieldInput.merchantId === "" && isIdInput) {
      setIsIdInput(false);
      setSearchFieldInput(INPUT_FIELD_INIT_STATE);
    }
  }, [searchFieldInput.merchantId]);

  useEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;
    initialFetchList(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, []);

  const initialFetchList = async (getIsOudated) => {
    // setLoading(true);
    setQueryMerchantLoading(true);
    const apiResponse = await queryMerchant(
      trimObject({
        page: pageSizeInfoRef.current.currentpage,
        size: pageSizeInfoRef.current.pagesize,
      }),
    );
    if (getIsOudated && getIsOudated()) return;
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }

    setSearch(false);
    setQueryMerchantLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput(INPUT_FIELD_INIT_STATE);
  };

  const resetError = () => {
    setError(ERROR_INIT_STATE);
  };

  const handleReset = () => {
    setIsIdInput(false);
    resetInputField();
    resetError();
    initialFetchList();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      let isSearchValid = true;
      if (searchFieldInput.startDate && !searchFieldInput.endDate) {
        setError((error) => ({ ...error, endDate: true }));
        isSearchValid = false;
      }
      if (searchFieldInput.endDate && !searchFieldInput.startDate) {
        setError((error) => ({ ...error, startDate: true }));
        isSearchValid = false;
      }
      if (searchFieldInput.startDate > searchFieldInput.endDate) {
        setError((error) => ({ ...error, invalidDateRange: true }));
        isSearchValid = false;
      }
      const startDate = moment(searchFieldInput.startDate).startOf("day");
      const endDate = moment(searchFieldInput.endDate).startOf("day");
      if (endDate.diff(startDate, "month", true) > 6) {
        setError((error) => ({ ...error, tooLargeDateRange: true }));
        isSearchValid = false;
      }

      if (!isSearchValid) return;
      const allValid = isAllInputValid(error.email);
      if (!allValid) {
        return;
      }
      resetError();
      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryMerchant(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
            setDatasource({
              total: 0,
              page: 0,
              size: 10,
              data: [],
            });
          }
        }
      }
    }

    setSearch(true);
    setSearching(false);
  };
  // ===================== search field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const isInputDisabled = (field) => {
    if (field !== "merchantId" && searchFieldInput.merchantId !== "")
      return true;
  };

  // ===================== search field function end ===================== //

  // ===================== column display function ===================== //

  const openColumnDisplay = (e) => {
    const position = e.target.getBoundingClientRect().top;
    setColumnDisplayDialog({
      open: true,
      position: position,
    });
    return;
  };

  const closeColumnDisplay = () => {
    setColumnDisplayDialog({
      ...columnDisplayDialog,
      open: false,
    });
    return;
  };

  // ===================== column display function ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const navigateToDetail = (data) => {
    console.log("Open merchant detail");
    const path = "/merchant-management/merchants/online-merchant-detail";
    const payload = {
      name:
        merchantManagement
          .find((tab) => tab.tabName === "Merchants")
          .page.find((page) => page.path === path).name + data.merchant_id,
      path: path,
      state: {
        merchantId: data.merchant_id,
      },
    };
    redirectFn("redirectPage", payload);
  };

  const columns = [
    {
      key: "merchant_id",
      title: t(PAGE_LOCALE_KEY + "columnTitle.merchantId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "name",
      title: t(PAGE_LOCALE_KEY + "columnTitle.name"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "email",
      title: t(PAGE_LOCALE_KEY + "columnTitle.email"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "create_time",
      title: t(PAGE_LOCALE_KEY + "columnTitle.createdDate"),
      sorter: false,
      render: (data, key) => {
        return dateTimeFormatter(checkNonMoneyDataForTable(data, key));
      },
    },
    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + "columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "action",
      title: t(PAGE_LOCALE_KEY + "columnTitle.action"),
      sorter: false,
      render: (data) => {
        const isTerminated = String(data?.status || "").toUpperCase() === "TERMINATED";
        return (
          <div className={s.actionBtnContainer}>
            {/* MAS-348 to hide merchant profile */}
            {/* <button
              onClick={() => {
                const path = "/merchant-management/merchants/merchant-profile";
                const payload = {
                  name:
                    merchantManagement
                      .find((tab) => tab.tabName === "Merchants")
                      .page.find((page) => page.path === path).name +
                    data.merchant_id,
                  path: path,
                  state: {
                    merchantId: data.merchant_id,
                  },
                };
                redirectFn("redirectPage", payload);
              }}
            >
              {t(PAGE_LOCALE_KEY + "columnTitle.actionLabel.profile")}
            </button> */}
            <button
              disabled={isTerminated}
              onClick={() => {
                const path =
                  "/merchant-management/employees/add-online-employee";
                const payload = {
                  name: merchantManagement
                    .find((tab) => tab.tabName === "Employees")
                    .page.find((page) => page.path === path).name,
                  path: path,
                  state: {
                    merchantId: data.merchant_id,
                  },
                };
                redirectFn("redirectPage", payload);
              }}
            >
              {t(PAGE_LOCALE_KEY + "columnTitle.actionLabel.addEmployee")}
            </button>

            <button
              onClick={() => {
                const path = "/transactions/online-orders";
                const payload = {
                  name: transactions.find((tab) => tab.path === path).tabName,
                  path: path,
                  state: {
                    id: data.merchant_id,
                  },
                };
                redirectFn("redirectPage", payload);
              }}
            >
              {" "}
              {t(PAGE_LOCALE_KEY + "columnTitle.actionLabel.payment")}
            </button>

            <button
              onClick={() => {
                const path = "/tools/online-statement-pdf";
                const payload = {
                  name: tools.find((tab) => tab.path === path).tabName,
                  path: path,
                  state: {
                    id: data.merchant_id,
                  },
                };
                redirectFn("redirectPage", payload);
              }}
            >
              {" "}
              {t(PAGE_LOCALE_KEY + "columnTitle.actionLabel.statement")}
            </button>
          </div>
        );
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.merchantId.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
            disabled={isInputDisabled("merchantId")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.name.label")}
            value={searchFieldInput.name}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "name")}
            onfocusFn={() => textInputOnFocus("name")}
            onblurFn={() => textInputOnBlur("name")}
            sxParams={{ textFocus: focus.name }}
            disabled={isInputDisabled("name")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.email.label")}
            value={searchFieldInput.email}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "email")}
            onfocusFn={() => textInputOnFocus("email")}
            onblurFn={() => textInputOnBlur("email")}
            sxParams={{ textFocus: focus.email }}
            disabled={isInputDisabled("email")}
          />

          <Selection
            label={t(PAGE_LOCALE_KEY + "search.status.label")}
            selectionValue={searchFieldInput.status}
            onchangeFn={(e) => textInputOnChange(e, "status")}
            onfocusFn={() => textInputOnFocus("status")}
            onblurFn={() => textInputOnBlur("status")}
            options={AgentStatusOptions("all")}
            sxParams={{ textFocus: focus.status, textError: error.status }}
            errorMessage={t(
              PAGE_LOCALE_KEY + "detailColumns.status.errorInput",
            )}
            disabled={isInputDisabled("status")}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={queryMerchantLoading || searching}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={queryMerchantLoading}
          filterStatus={columnDisplayDialog}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          handleColumnDisplay={openColumnDisplay}
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "online-merchants",
          }}
        />
        <div
          className={[
            s.datatableContainer,
            queryMerchantLoading ? s.loadingNow : "",
          ].join(" ")}
        >
          {queryMerchantLoading ? (
            <Spin />
          ) : (
            // TODO column sorting
            <Datatable
              navigateToDetail={navigateToDetail}
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setPageSize={setPageSize}
              setCurrentPage={setCurrentPage}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}
          <ColumnFilter
            columnDisplayDialog={columnDisplayDialog}
            dialogOncloseFn={closeColumnDisplay}
            columns={columns}
            columnsArray={columnsArray}
            rememberPreference="online-merchants"
            setColumnsArray={setColumnsArray}
          />
          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};
export default React.memo(OnlineMerchants);
